import {
  createRouter,
  createWebHashHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from 'vue-router';
import { connectTileId, reportConnectTile, useAccountConfig } from '@blpde/connect';
import { useTileWorkspaces } from '@/tiles/useTileWorkspaces';
import { installLoadFailureRecovery } from '@/loadFailure';
import { installTileFrame, TILE_CLOSING_PATH } from '@/tiles/frame';
import HomeView from '@/views/HomeView.vue';
import LoginView from '@/views/LoginView.vue';
import {
  adoptToken,
  stageTokenAdoption,
  ensureValidStoredSession,
  isAuthenticated,
  reloadShellInto,
} from '@/auth';
import {
  accountApplicationsAuthoritative,
  accountApplicationsLoaded,
  isAppEntitled,
  resetAccountApplications,
  refreshAccountApplications,
} from '@/applications';
import { apps } from '@/integration';
import { appPlaceholderRoute, installAppLoaderGuard } from '@/integration/loader';
import { loadDeepLinks, usePermissionStore } from '@erp/connect/shell';
import { resolveErpPermissionGate } from '@/integration/erpPermissionGate';
import { hasPendingSupportLink, onSupportLinkArrived } from '@/supportLink';
import { isConnectSettingsTabId, shellSettingsModuleOf } from '@/settingsHub/connectSettings';
import { SETTINGS_HUB_ROUTE, SETTINGS_LIST_ROUTE, SETTINGS_TOPIC_ROUTE } from '@/settingsHub/hub';

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'ConnectHome', component: HomeView },
  {
    path: '/login',
    name: 'ConnectLogin',
    component: LoginView,
    meta: { public: true },
  },
  // Cross-module mention inbox — shell-owned, not a sub-app route, because
  // it aggregates mentions from every module via master's /v1/mentions.
  {
    path: '/mentions',
    name: 'ConnectMentions',
    component: () => import('@/views/MentionsView.vue'),
  },
  // Every notification of the account, read or not, and where they are
  // deleted (I-2218); the bell lists the unread ones.
  {
    path: '/notifications',
    name: 'ConnectNotifications',
    component: () => import('@/views/NotificationsView.vue'),
  },
  // Legacy B-DMS producer deep links (master mentions stored `/bdms/correspondences/N`).
  // Not a real screen — resolve the host record and redirect into ERP.
  // Tab selection runs in router.afterEach (see selectBdmsHostTabWhenReady).
  {
    path: '/bdms/correspondences/:id(\\d+)',
    name: 'BdmsCorrespondenceDeepLink',
    component: { render: () => null },
    beforeEnter: async (to) => {
      const id = Number(to.params.id);
      if (!id) return { path: '/' };
      const { buildCorrespondenceDeepLink, hostIdentityFromShellPath, rememberBdmsFocus } =
        await loadDeepLinks();
      const resolved = await buildCorrespondenceDeepLink(id);
      if (!resolved) return { path: '/mentions' };
      const [pathname, qs] = resolved.split('?');
      const host = hostIdentityFromShellPath(pathname);
      rememberBdmsFocus({ correspondenceId: id, ...(host ?? {}) });
      return {
        path: pathname,
        query: Object.fromEntries(new URLSearchParams(qs || '')),
      };
    },
  },
  // The settings hub (I-2020): every module's settings topics in one searchable
  // overview (connect/src/settingsHub). Shell-owned, not a hosted module, so it
  // stays out of the app registry and the availability gate. `?open=<module>`
  // opens that module's group. Connect's own settings used to live here with
  // `?tab=`; such links land on that topic — „Darstellung" (`appearance`) on
  // its section of the one settings list (I-2510).
  {
    path: '/settings',
    name: SETTINGS_HUB_ROUTE,
    component: () => import('@/views/SettingsHubView.vue'),
    beforeEnter: (to) => {
      if (isConnectSettingsTabId(to.query.tab)) {
        return {
          name: SETTINGS_TOPIC_ROUTE,
          params: { app: shellSettingsModuleOf(to.query.tab), topic: to.query.tab },
          replace: true,
        };
      }
      if (to.query.tab === 'appearance') {
        return { name: SETTINGS_LIST_ROUTE, params: { section: 'appearance' }, replace: true };
      }
      // The look designer's old topic id, now „Design“ (`design`).
      if (to.query.tab === 'look') {
        return { name: SETTINGS_TOPIC_ROUTE, params: { app: 'general', topic: 'design' }, replace: true };
      }
      return true;
    },
  },
  // The one settings list (I-2510): every scalar setting of Connect and of the
  // apps, a section per `:section`; `?scope=` the edited layer. Its static
  // segment outranks the module crumb below.
  {
    path: '/settings/list/:section?',
    name: SETTINGS_LIST_ROUTE,
    component: () => import('@/views/SettingsListView.vue'),
  },
  // A module's own crumb: no page of its own, the hub with the module open.
  {
    path: '/settings/:app',
    redirect: (to) => ({ name: SETTINGS_HUB_ROUTE, query: { open: String(to.params.app) } }),
  },
  // Pages that moved into „Allgemein“ (I-3006) keep their old addresses, so
  // bookmarks, links and the help still land: the overview and the look
  // designer („Design“, once `look`) left „System“, the dictionary left Write.
  {
    path: '/settings/system/general',
    redirect: { name: SETTINGS_TOPIC_ROUTE, params: { app: 'general', topic: 'general' } },
  },
  {
    path: '/settings/system/design',
    redirect: { name: SETTINGS_TOPIC_ROUTE, params: { app: 'general', topic: 'design' } },
  },
  {
    path: '/settings/system/look',
    redirect: { name: SETTINGS_TOPIC_ROUTE, params: { app: 'general', topic: 'design' } },
  },
  {
    path: '/settings/write/personalDictionary',
    redirect: { name: SETTINGS_TOPIC_ROUTE, params: { app: 'general', topic: 'personalDictionary' } },
  },
  // One topic page: the module's panel, loaded from its `@<app>/connect` module
  // (Connect's own for `general`, `system` and `development`).
  {
    path: '/settings/:app/:topic',
    name: SETTINGS_TOPIC_ROUTE,
    component: () => import('@/views/SettingsTopicView.vue'),
  },
  {
    path: '/downloads',
    name: 'ConnectDownloads',
    component: () => import('@/views/DownloadsView.vue'),
  },
  {
    path: '/changelog',
    name: 'ConnectChangelog',
    component: () => import('@/views/ChangelogView.vue'),
  },
  // The component showcase of the UI kit for all of Connect (I-2032; it used to
  // live in the ERP under /erp/nx/showcase, which now points here).
  {
    path: '/showcase',
    name: 'ConnectShowcase',
    component: () => import('@/showcase/ShowcaseView.vue'),
  },
  {
    path: '/formica_id',
    name: 'ConnectFormicaId',
    component: () => import('@/views/FormicaIdView.vue'),
  },
  // Shell-owned person profile — link target for AccountChip / people mentions.
  // Optional :accountId; query may carry name/email/firstName/lastName when the
  // person has no master login yet (employee-only).
  {
    path: '/people/:accountId?',
    name: 'ConnectPeopleProfile',
    component: () => import('@/views/PeopleProfileView.vue'),
  },
  // Inside a tile: the empty page a closing tile leaves to (`tiles/frame.ts`).
  { path: TILE_CLOSING_PATH, component: { render: () => null } },
  // The workspaces live in the shell layout and stay mounted; this only opens them.
  {
    path: '/tiles',
    name: 'connect.tiles',
    component: { render: () => null },
  },
  // Hosted apps load on first use: a placeholder below each base path stands in
  // until the loader guard mounts the app's own routes (integration/loader.ts).
  ...apps.map(appPlaceholderRoute),
];

/** Pages without the shell around them: a window of their own, a device's terminal. */
const OWN_PAGE_PATHS = ['/erp/nx/layout-designer', '/timemanagement/terminal'];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// A support link pasted into a tab that is already open (supportLink.ts): the
// sign-in view redeems it, wherever the tab was.
onSupportLinkArrived(() => {
  if (router.currentRoute.value.name !== 'ConnectLogin') void router.push({ name: 'ConnectLogin' });
});

// A remote support link waits for the sign-in view (it was taken out of the
// address before the router started): every route leads there — even for a
// browser that is signed in, whose own session the link replaces — and without
// a `redirect`, so the link cannot travel on in the address. Registered first,
// before the token capture and the app loader; the guards after it keep the
// exception (no token adopted, no bounce home from the sign-in view).
router.beforeEach((to) => {
  if (hasPendingSupportLink()) {
    return to.name === 'ConnectLogin' ? true : { name: 'ConnectLogin' };
  }
  return true;
});

// Token capture comes next: a hosted app that needs the API to load (the ERP) reads
// the bearer the URL carried. Not while a support link waits: the link replaces
// the session, and a token that replaced it first would reload the page before
// the sign-in view could redeem the link.
router.beforeEach((to) => (hasPendingSupportLink() ? true : captureUrlToken(to) ?? true));

// A tile never shows the login: the session ended for the whole page around it.
router.beforeEach((to) => {
  if (!connectTileId() || to.name !== 'ConnectLogin') return true;
  window.top?.location.reload();
  return false;
});
// The workspace reopens a tile where it was; `/tiles` inside a tile would nest them.
router.beforeEach((to) => (connectTileId() && to.path === '/tiles' ? { path: '/' } : true));
router.afterEach((to, _from, failure) => {
  // Not there after all (a guard, or the record opener taking it elsewhere).
  if (failure) return;
  if (to.path !== TILE_CLOSING_PATH) reportConnectTile({ path: to.fullPath });
});
if (connectTileId()) installTileFrame(router);
installLoadFailureRecovery(router);

// With tabs and tiles every page lives in a tab: the page around the tiles only
// holds the tabs, and loads no app of its own. A public page or a window of its own
// (the layout designer) stays a page.
router.beforeEach(async (to) => {
  if (
    connectTileId() ||
    to.path === '/tiles' ||
    to.meta.public ||
    to.meta.hideShellChrome ||
    to.name === 'ConnectLogin' ||
    OWN_PAGE_PATHS.some((path) => to.path.startsWith(path)) ||
    !isAuthenticated() ||
    !(await ensureValidStoredSession())
  )
    return true;
  const shell = useAccountConfig<{ tiles?: { enabled?: boolean } }>('shell');
  const switchedOff = () => shell.effective.value?.tiles?.enabled === false;
  // Known from the last visit: no waiting for master, and `ShellLayout` follows its answer.
  if (switchedOff()) return true;
  if (!shell.loaded.value) await shell.reload().catch(() => undefined);
  if (switchedOff()) return true;
  useTileWorkspaces().openRoute(to.fullPath);
  return { path: '/tiles' };
});

// Registered next, so every later guard sees an app's real route, not its placeholder.
// An app that needs a session to load is only loaded for a valid one; otherwise the
// navigation reaches the auth gate below, which sends it to the login.
installAppLoaderGuard(
  router,
  async () => isAuthenticated() && (await ensureValidStoredSession())
);

// After navigating to a host record with B-DMS focus query, select the dossier
// tab once the layout registry has mounted the tab set (async, polled).
router.afterEach((to) => {
  const q = to.query;
  if (q.tab === 'bdms' || q.bdmsCorrespondence != null || q.bdmsActivity != null) {
    void loadDeepLinks().then((m) => m.selectBdmsHostTabWhenReady());
  }
});

/**
 * Sub-app route names are prefixed (`forge.Inbox`, `mail.Mail.Mailbox`, …) so
 * they don't collide globally. Sub-app components still write the un-prefixed
 * name (`router.push({ name: 'Inbox' })`, `<RouterLink :to="{ name: 'Mail.Mailbox', params }">`)
 * because they must also run standalone.
 *
 * Bridge: monkey-patch `router.resolve` to map an un-prefixed name to the
 * current app's namespace. Both `router.push` and RouterLink flow through
 * `router.resolve`, so this single hook covers them — and crucially the
 * caller's `params` / `query` pass through untouched (a previous alias-route
 * approach silently dropped params on the redirect hop).
 */
/**
 * Sub-app route names are prefixed to avoid global collisions, but sub-app
 * components still write un-prefixed names (`{ name: 'Inbox' }`,
 * `{ name: 'Mail.Mailbox', params }`). We bridge both render-time (RouterLink
 * resolve) and navigation-time (push/replace) by:
 *
 *  1. Overriding `router.resolve` — covers RouterLink active-class, href, and
 *     anything that calls resolve at render time.
 *  2. A `beforeEach` as a safety net — handles cases where vue-router calls
 *     into matcher.resolve directly without going through router.resolve.
 *
 * Both map an un-prefixed name to `<currentApp>.<name>` based on `from.path`
 * (or `router.currentRoute.value.path` for resolve). Params/query/hash are
 * passed through untouched.
 */
/**
 * Sub-app route names are prefixed (`forge.Inbox`, `mail.Mail.Mailbox`, …) to
 * avoid global collisions, but sub-app components still write un-prefixed
 * names (`{ name: 'Inbox' }`, `{ name: 'Mail.Mailbox', params }`) so they
 * also work standalone.
 *
 * Vue-router's push/replace call into an internal `resolve` closure that
 * monkey-patching `router.resolve` does NOT intercept (throws "No match
 * for …" synchronously). So we override push/replace at the property level,
 * map the name to the current app's namespace BEFORE delegating to the
 * original, then params/query survive. We also keep a `router.resolve`
 * override so RouterLink (which calls `router.resolve` directly for
 * href/active-class) renders correctly.
 */
const APP_IDS = apps.map((a) => a.id);

function rewriteIfUnknownName<T>(location: T): T {
  if (
    !location ||
    typeof location !== 'object' ||
    !('name' in (location as Record<string, unknown>))
  ) {
    return location;
  }
  const loc = location as Record<string, unknown>;
  if (typeof loc.name !== 'string') return location;
  if (router.hasRoute(loc.name)) return location;
  const ctxPath = router.currentRoute?.value?.path || '';
  const app = APP_IDS.find(
    (id) => ctxPath === `/${id}` || ctxPath.startsWith(`/${id}/`),
  );
  if (!app) return location;
  const prefixed = `${app}.${loc.name}`;
  if (!router.hasRoute(prefixed)) return location;
  return { ...loc, name: prefixed } as T;
}

const originalPush = router.push.bind(router);
const originalReplace = router.replace.bind(router);
const originalResolve = router.resolve.bind(router);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(router as any).push = function (to: any) {
  try {
    return originalPush(rewriteIfUnknownName(to));
  } catch (err) {
    console.warn('[connect-router] push failed for', to, err);
    return Promise.resolve();
  }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(router as any).replace = function (to: any) {
  try {
    return originalReplace(rewriteIfUnknownName(to));
  } catch (err) {
    console.warn('[connect-router] replace failed for', to, err);
    return Promise.resolve();
  }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(router as any).resolve = function (to: any, currentLocation: any) {
  try {
    return originalResolve(rewriteIfUnknownName(to), currentLocation);
  } catch (err) {
    // Unknown route name — return a no-match resolution rather than throw,
    // so RouterLink falls back to `/` instead of crashing the whole tree
    // (a sub-app may reference a name that hasn't been routed yet).
    console.warn('[connect-router] resolve fallback for', to, err);
    return originalResolve('/', currentLocation);
  }
};

/**
 * Token capture at the shell level. When a sub-app is opened with a `?token=`
 * query (e.g. from the master gateway redirect), stash the bearer into the
 * shared `blp_token` key (plus the `nx_token` mirror erp still reads).
 *
 * Runs as its own guard, registered BEFORE the app loader guard: an app whose
 * load talks to the API (the ERP's definitions) must find the captured bearer.
 */
function captureUrlToken(to: RouteLocationNormalized): RouteLocationRaw | false | undefined {
  // Token capture from a `?token=` URL param (master-gateway redirect flow).
  // Written to the shared `blp_token`; `nx_token` is the one remaining per-app
  // mirror (erp's api/api.ts reads it directly). The bearer is the only
  // credential: a session UUID never authenticates, so none is captured.
  const params = new URLSearchParams(window.location.search);
  const token = (to.query.token as string | undefined) || params.get('token');
  if (token) {
    // A new session: replaced like a sign-in (hook, previous state and login
    // marker gone), not just overwritten in place.
    // A token that would replace a DIFFERENT session is not installed into the
    // running shell: it is staged for the next boot (see `stageTokenAdoption`).
    const replacesSession = isAuthenticated() && localStorage.getItem('blp_token') !== token;
    const credentialsChanged = replacesSession ? false : adoptToken(token);
    if (credentialsChanged) {
      usePermissionStore().clear();
      // Drop shell app grants so the previous account's snapshot cannot leak.
      resetAccountApplications();
    }
    // Strip the token from BOTH window.search and the hash route query so
    // credentials never remain in the address bar / history / copy-paste URLs.
    const cleaned = new URLSearchParams(window.location.search);
    cleaned.delete('token');
    const qs = cleaned.toString();
    const nextQuery = { ...to.query } as Record<string, unknown>;
    delete nextQuery.token;
    const hashPath = to.path.startsWith('/') ? to.path : `/${to.path}`;
    const hashQs = new URLSearchParams();
    for (const [k, v] of Object.entries(nextQuery)) {
      if (v == null || v === '') continue;
      if (Array.isArray(v)) {
        for (const item of v) hashQs.append(k, String(item));
      } else {
        hashQs.set(k, String(v));
      }
    }
    const hashQuery = hashQs.toString();
    const target = `${window.location.pathname}${qs ? `?${qs}` : ''}#${hashPath}${hashQuery ? `?${hashQuery}` : ''}`;
    window.history.replaceState(null, '', target);
    if (replacesSession) {
      // The address is clean; reload into it (a cancelled reload leaves the
      // current session exactly as it was).
      stageTokenAdoption(token);
      reloadShellInto(target);
      return false;
    }
    // Also clear the token from the vue-router location for this navigation.
    if (to.query.token) {
      return {
        path: to.path,
        query: nextQuery as typeof to.query,
        hash: to.hash,
        replace: true,
      };
    }
  }
}

router.beforeEach(async (to) => {
  // Auth gate: unauthenticated users land on /login (preserving the
  // requested route as ?redirect for post-login navigation).
  //
  // A stored token is not proof of a live session — it can have been revoked
  // elsewhere (signed out in another app). `ensureValidStoredSession` asks
  // master once per page load and drops the local state when it is rejected, so
  // a dead session shows the login view instead of an empty shell (I-385).
  if (!to.meta.public && to.name !== 'ConnectLogin') {
    const loginRedirect = {
      name: 'ConnectLogin',
      query:
        to.fullPath && to.fullPath !== '/'
          ? { redirect: to.fullPath }
          : undefined,
    };
    if (!isAuthenticated()) return loginRedirect;
    if (!(await ensureValidStoredSession())) return loginRedirect;
  }
  // Already logged in but trying to visit /login → bounce home. Validated
  // first, otherwise a stale token would ping-pong login → home → login. Not
  // while a remote support link waits: the sign-in view redeems it for a
  // browser that is signed in too, whose session the link replaces (the
  // support guard above sends every route there).
  if (
    to.name === 'ConnectLogin' &&
    !hasPendingSupportLink() &&
    isAuthenticated() &&
    (await ensureValidStoredSession())
  ) {
    return { path: '/' };
  }

  // Account application grants: shell apps (erp, designer, …) need an
  // active grant / connect.app.* permission. Wait for the first snapshot if
  // needed, but never bounce to home when the fetch failed (loaded without
  // data) mid-click — that caused "first click reloads home, second works".
  if (isAuthenticated() && !to.meta.public) {
    const segment = to.path.split('/').filter(Boolean)[0];
    const app = apps.find((a) => a.id === segment);
    if (app) {
      if (!accountApplicationsLoaded()) {
        try {
          await refreshAccountApplications();
        } catch {
          // Fail open for this navigation; shell will retry.
        }
      }
      // Only deny when we have a successful grants snapshot that excludes the app.
      // Transient fetch failures stay fail-open (authoritative=false).
      if (accountApplicationsAuthoritative() && !isAppEntitled(app.id)) {
        // Use replace so the failed attempt is not stuck in history as a
        // "flash of calc" + back. Stay on hash home without full reload.
        return { path: '/', query: { denied: app.id }, replace: true };
      }
    }
  }

  if (
    to.path.startsWith('/erp') &&
    to.name !== 'erp.PermissionDenied' &&
    to.name !== 'erp.ServiceUnavailable' &&
    to.name !== 'erp.NoEmployee' &&
    !to.meta.public &&
    isAuthenticated()
  ) {
    const permissions = usePermissionStore();
    try {
      await permissions.ensureLoaded();
    } catch (err) {
      // Resilience decision: a failed `/me/permissions` (e.g. the erp module
      // momentarily unreachable in the all-in-one node) must NOT lock the user
      // out of the whole ERP shell. We proceed — but:
      //   - routes that declare `meta.requiredPermissions` are routed by the
      //     gate below to the "service unavailable" page (load failure is an
      //     availability problem, not an authorization verdict — BLP/nx#1139);
      //   - the BACKEND stays the real authorization wall: every erp/forge API
      //     call is independently authenticated + authorized server-side, so a
      //     frontend that renders an intentionally-ungated surface (the tasks
      //     page) cannot read data the caller may not see.
      // Logged at error so an operator can alert on a degraded permission path;
      // the store retries on the next navigation.
      console.error('[erp guard] permission load failed; gated routes go to service-unavailable:', err);
    }

    const gate = resolveErpPermissionGate(to, permissions);
    if (gate !== true) return gate;
  }

  return true;
});

export default router;
